import React from 'react';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { createPortal } from 'react-dom';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import Button from './Button';

type ConfirmationDialogType = 'default' | 'danger';

interface TypeStyle {
  icon: IconDefinition;
  iconColor: string;
  confirmVariant: 'primary' | 'danger';
  iconBg: string;
}

interface ConfirmationDialogProps {
  title: string;
  message: string;
  onConfirm: () => void;
  onCancel: () => void;
  confirmText?: string;
  cancelText?: string;
  type?: ConfirmationDialogType;
}

function ConfirmationDialog({
  title,
  message,
  onConfirm,
  onCancel,
  confirmText = 'Confirmar',
  cancelText = 'Cancelar',
  type = 'default'
}: ConfirmationDialogProps): React.ReactElement {
  const typeStyles: Record<ConfirmationDialogType, TypeStyle> = {
    danger: {
      icon: faExclamationTriangle,
      iconColor: 'text-red-600',
      confirmVariant: 'danger',
      iconBg: 'bg-red-100'
    },
    default: {
      icon: faExclamationTriangle,
      iconColor: 'text-blue-600',
      confirmVariant: 'primary',
      iconBg: 'bg-blue-100'
    }
  };

  const currentType = typeStyles[type] || typeStyles.default;

  const handleBackdropClick = (e: React.MouseEvent): void => {
    if (e.target === e.currentTarget) {
      onCancel();
    }
  };

  const dialogContent = (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 transform transition-all">
        <div className="p-6">
          <div className="flex items-center space-x-4">
            <div className={`${currentType.iconBg} p-3 rounded-full flex-shrink-0`}>
              <FontAwesomeIcon
                icon={currentType.icon}
                className={`w-6 h-6 ${currentType.iconColor}`}
              />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
              <p className="text-gray-600">{message}</p>
            </div>
          </div>

          <div className="flex justify-end space-x-3 mt-6">
            <Button
              onClick={onCancel}
              variant="secondary"
            >
              {cancelText}
            </Button>
            <Button
              onClick={onConfirm}
              variant={currentType.confirmVariant}
            >
              {confirmText}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );

  // Use React Portal to render the dialog at the document body level
  return createPortal(dialogContent, document.body);
}

export default ConfirmationDialog;